import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Wallet, TrendingUp, TrendingDown, DollarSign, PieChart } from "lucide-react";
import { cn } from "../lib/utils";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, ReferenceLine } from "recharts";

function Stat({ icon: Icon, label, value, tint, sub }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">
        <Icon className={cn("h-3.5 w-3.5", tint)} /> {label}
      </div>
      <div className={cn("text-2xl font-bold font-mono", tint)}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

export default function Portfolio() {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sellQty, setSellQty] = useState({});

  const load = () => api.get("/portfolio").then(({ data }) => {
    setData(data);
    if (data.new_milestones && data.new_milestones.length) {
      data.new_milestones.forEach((m) => toast.success(`🏆 Milestone unlocked: ${m}!`));
      refreshUser();
    }
  }).finally(() => setLoading(false));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sell = async (ticker) => {
    const shares = Number(sellQty[ticker]);
    if (!shares || shares <= 0) { toast.error("Enter shares to sell"); return; }
    try {
      await api.post("/portfolio/trade", { ticker, side: "sell", shares });
      toast.success(`Sold ${shares} ${ticker} (virtual)`);
      setSellQty((s) => ({ ...s, [ticker]: "" }));
      load(); refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sell failed");
    }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-28 w-full rounded-2xl" /><Skeleton className="h-80 w-full rounded-2xl" /></div>;

  const plPositive = data.total_pl >= 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading tracking-tight">Paper Portfolio</h1>
          <p className="text-sm text-muted-foreground mt-1">Virtual $100,000 to practice. No real money involved.</p>
        </div>
        <Button onClick={() => navigate("/dashboard")} className="bg-sky-500 hover:bg-sky-600 text-white gap-1.5" data-testid="go-buy-btn">
          <TrendingUp className="h-4 w-4" /> Buy from leaderboard
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={PieChart} label="Total Value" value={`$${data.total_value.toLocaleString()}`} tint="text-sky-500" />
        <Stat icon={DollarSign} label="Cash" value={`$${data.cash.toLocaleString()}`} tint="text-foreground" />
        <Stat icon={plPositive ? TrendingUp : TrendingDown} label="Total P/L"
          value={`${plPositive ? "+" : ""}$${data.total_pl.toLocaleString()}`}
          tint={plPositive ? "text-emerald-500" : "text-red-500"} />
        <Stat icon={Wallet} label="Return" value={`${plPositive ? "+" : ""}${data.total_pl_pct}%`}
          tint={plPositive ? "text-emerald-500" : "text-red-500"} sub="vs starting cash" />
      </div>

      {/* Portfolio value over time */}
      <div className="rounded-2xl border border-border bg-card p-5" data-testid="portfolio-chart">
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Portfolio Value Over Time</p>
          <span className={cn("text-xs font-mono font-semibold", plPositive ? "text-emerald-500" : "text-red-500")}>
            {plPositive ? "+" : ""}{data.total_pl_pct}%
          </span>
        </div>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={(data.history || []).map((h) => ({
              t: h.ts ? new Date(h.ts).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "",
              value: h.value,
            }))} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
              <defs>
                <linearGradient id="pv" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0EA5E9" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#0EA5E9" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="t" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} stroke="hsl(var(--muted-foreground))" minTickGap={24} />
              <YAxis domain={["auto", "auto"]} tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} stroke="hsl(var(--muted-foreground))"
                width={70} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => [`$${Number(v).toLocaleString()}`, "Value"]}
                contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }} />
              <ReferenceLine y={100000} stroke="hsl(var(--muted-foreground))" strokeDasharray="4 4" />
              <Area type="monotone" dataKey="value" stroke="#0EA5E9" strokeWidth={2.5} fill="url(#pv)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <p className="text-[11px] text-muted-foreground mt-2 text-center">
          Baseline $100,000 · each point is a paper trade; the last point is today's live value.
        </p>
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="px-5 py-3 border-b border-border text-xs font-mono uppercase tracking-widest text-muted-foreground">Open Positions</div>
        {data.positions.length === 0 ? (
          <div className="px-5 py-12 text-center text-muted-foreground" data-testid="empty-positions">
            No positions yet. Open a stock from the leaderboard and buy some virtual shares.
          </div>
        ) : (
          <div className="divide-y divide-border/60" data-testid="positions-list">
            {data.positions.map((p) => {
              const pos = p.pl >= 0;
              return (
                <div key={p.ticker} className="px-5 py-4 flex flex-wrap items-center gap-4" data-testid={`position-${p.ticker}`}>
                  <div className="flex-1 min-w-[120px]">
                    <div className="font-mono font-bold">{p.ticker}</div>
                    <div className="text-xs text-muted-foreground">{p.shares} sh @ ${p.avg_price}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm">${p.market_value.toLocaleString()}</div>
                    <div className={cn("text-xs font-mono", pos ? "text-emerald-500" : "text-red-500")}>
                      {pos ? "+" : ""}{p.pl_pct}% (${p.pl})
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input type="number" min="1" placeholder="Qty" data-testid={`sell-qty-${p.ticker}`}
                      value={sellQty[p.ticker] || ""} onChange={(e) => setSellQty((s) => ({ ...s, [p.ticker]: e.target.value }))}
                      className="w-20 h-9" />
                    <Button size="sm" variant="outline" data-testid={`sell-btn-${p.ticker}`}
                      onClick={() => sell(p.ticker)} className="border-red-500/30 text-red-500 hover:bg-red-500/10">Sell</Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="px-5 py-3 border-b border-border text-xs font-mono uppercase tracking-widest text-muted-foreground">Recent Trades</div>
        {data.trades.length === 0 ? (
          <div className="px-5 py-8 text-center text-muted-foreground text-sm">No trades yet.</div>
        ) : (
          <div className="divide-y divide-border/60">
            {data.trades.map((t, i) => (
              <div key={i} className="px-5 py-3 flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className={cn("text-xs font-bold uppercase px-2 py-0.5 rounded-md",
                    t.side === "buy" ? "bg-emerald-500/10 text-emerald-500" : "bg-red-500/10 text-red-500")}>{t.side}</span>
                  <span className="font-mono font-semibold">{t.ticker}</span>
                  <span className="text-muted-foreground text-xs">{t.shares} sh @ ${t.price}</span>
                </div>
                <span className="font-mono text-xs text-muted-foreground">${t.amount.toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
